import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';
import { useForm } from 'react-hook-form';

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
} from '@mui/material';

import {
  GoogleAdsCampaignConfigItem,
  createGoogleAdsCampaignConfig,
  updateGoogleAdsCampaignConfig,
} from '~/services/PublicOfferFeedService';

type Props = {
  open: boolean;
  setOpen: (open: boolean) => void;
  fetchData: () => void;
  editableRow?: GoogleAdsCampaignConfigItem | null;
};

export default function GoogleAdsCampaignsConfigForm({ open, setOpen, fetchData, editableRow }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const isEditing = !!editableRow;

  const [customerId, setCustomerId] = useState<string>(editableRow?.customer_id ?? '');
  const [campaignId, setCampaignId] = useState<string>(editableRow?.campaign_id ?? '');
  const [enabled, setEnabled] = useState(false);
  const [campaignType, setCampaignType] = useState<'offer' | 'search'>(editableRow?.campaign_type ?? 'offer');

  const { handleSubmit } = useForm<Record<string, unknown>>();

  const onCreate = useCallback(async () => {
    try {
      const resp = await createGoogleAdsCampaignConfig({
        customer_id: customerId,
        campaign_id: campaignId,
        campaign_type: campaignType,
        enabled,
      });
      if (resp.status !== 201) {
        enqueueSnackbar('Failed to create campaign config', { variant: 'error' });
        return;
      }
      enqueueSnackbar('Campaign config created', { variant: 'success' });
      fetchData();
      setOpen(false);
    } catch (e) {
      enqueueSnackbar('Failed to create campaign config', { variant: 'error' });
    }
  }, [customerId, campaignId, campaignType, enqueueSnackbar, fetchData, setOpen, enabled]);

  const onUpdate = useCallback(async () => {
    try {
      const resp = await updateGoogleAdsCampaignConfig(campaignId, {
        customer_id: customerId,
        campaign_type: campaignType,
        enabled,
      });
      if (resp.status !== 200) {
        enqueueSnackbar('Failed to update campaign config', { variant: 'error' });
        return;
      }
      enqueueSnackbar('Campaign config updated', { variant: 'success' });
      fetchData();
      setOpen(false);
    } catch (e) {
      enqueueSnackbar('Failed to update campaign config', { variant: 'error' });
    }
  }, [campaignId, customerId, campaignType, enqueueSnackbar, fetchData, setOpen, enabled]);

  const onSubmit = useMemo(() => (isEditing ? onUpdate : onCreate), [isEditing, onCreate, onUpdate]);

  useEffect(() => {
    if (editableRow) {
      setCustomerId(editableRow.customer_id);
      setCampaignId(editableRow.campaign_id);
      setCampaignType(editableRow.campaign_type);
      setEnabled(editableRow.enabled);
    }
  }, [editableRow]);

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={open}
      onClose={() => setOpen(false)}
      component="form"
      onSubmit={handleSubmit(onSubmit)}
    >
      <DialogTitle>{isEditing ? 'Edit' : 'Create'} Campaign Config</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12 }}>
            <TextField
              id="customer_id"
              label="Customer ID"
              variant="outlined"
              value={customerId}
              onChange={(e) => setCustomerId(e.target.value)}
              required
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              id="campaign_id"
              label="Campaign ID"
              variant="outlined"
              value={campaignId}
              onChange={(e) => setCampaignId(e.target.value)}
              required
              disabled={isEditing}
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={enabled}
                  onChange={(e) => setEnabled(e.target.checked)}
                  name="enabled"
                  color="primary"
                />
              }
              label="Enabled"
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <FormControl fullWidth required>
              <InputLabel id="campaign_type_label">Campaign Type</InputLabel>
              <Select
                id="campaign_type"
                labelId="campaign_type_label"
                label="Campaign Type"
                value={campaignType}
                onChange={(e) => setCampaignType(e.target.value)}
              >
                <MenuItem value="offer">Offer</MenuItem>
                <MenuItem value="search">Search</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={() => setOpen(false)}>
          Cancel
        </Button>
        <Button variant="contained" type="submit">
          {isEditing ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
